import { useMemo } from 'react'
import { Button } from '@/components/catalyst-ui/button'
import { ArrowDownIcon } from '@heroicons/react/24/solid'
import { Badge } from '@/components/catalyst-ui/badge'
import { RadioGroup, RadioField, Radio } from '@/components/catalyst-ui/radio'
import { Field, Label } from '@/components/catalyst-ui/fieldset'
import { Input } from '@/components/catalyst-ui/input'
import { Checkbox, CheckboxField } from '@/components/catalyst-ui/checkbox'
import { DialogTitle } from '@/components/catalyst-ui/dialog'

export function ExportCodeTab({
  exportFlow,
  assetsCount,
  isAiCompanionMode,
}) {
  const showAssetsCheckbox = useMemo(() => {
    if (isAiCompanionMode) {
      return typeof assetsCount === 'number' && assetsCount > 0 && exportFlow.exportFormat === 'sourceCode'
    }
    return typeof assetsCount === 'number' && assetsCount > 0 && (exportFlow.exportFormat === 'html' || exportFlow.exportFormat === 'react')
  }, [assetsCount, exportFlow.exportFormat, isAiCompanionMode])

  const exportButtonText = useMemo(() => {
    if (exportFlow.isExporting) return 'Exporting...'
    return exportFlow.exportAllPages ? 'Download Full Project' : 'Download Current Page'
  }, [exportFlow.isExporting, exportFlow.exportAllPages])

  const exportFormats = useMemo(() => isAiCompanionMode 
    ? [
        { value: 'sourceCode', label: 'Source Code', description: 'Best for static sites and landing pages' },
        { value: 'deployment', label: 'Deployment', description: 'Best for interactive web apps.  ' },
      ]
    : [
        { value: 'html', label: 'HTML & CSS', description: 'Best for static sites and landing pages' },
        { value: 'react', label: 'React', description: 'Best for interactive web apps.' },
      ], [isAiCompanionMode])

      
  return (
    <div className="py-4 space-y-1 bg-[#FAFAFA] rounded-b-xl">
      <div className="flex items-center justify-between border-b border-[#E4E4E7] pb-2 px-4">
        <DialogTitle className="!text-sm !font-medium text-[#18181B] mb-1">Export Code Settings</DialogTitle>
      </div>

      <div className="space-y-2 px-4 py-2">
        <Field>
          <Label className="!text-sm !font-semibold text-[#09090B]">File name</Label>
          <Input
            type="text"
            value={exportFlow.exportFileName}
            onChange={(e) => exportFlow.setExportFileName(e.target.value)}
            placeholder="Your project name"
            className="mb-4"
          />
        </Field>
        {!isAiCompanionMode && (
          <CheckboxField>
            <Checkbox checked={exportFlow.exportAllPages} onChange={(checked) => exportFlow.setExportAllPages(checked)} />
            <Label className="!text-sm !font-semibold text-[#09090B]">Export all pages</Label>
          </CheckboxField>
        )}
        {showAssetsCheckbox && !isAiCompanionMode && (
          <CheckboxField>
            <Checkbox checked={exportFlow.downloadableAssets} onChange={(checked) => exportFlow.setDownloadableAssets(checked)} />
            <Label className="!text-sm !font-semibold text-[#09090B]">
              <span className="block">Include assets</span>
            </Label>
          </CheckboxField>
        )}
      </div>

      <div className="space-y-2 px-4 border-y border-[#E4E4E7] py-4">
        <div className="text-sm font-semibold text-[#3F3F46]">Export Format</div>
        <RadioGroup value={exportFlow.exportFormat} onChange={exportFlow.setExportFormat} className="space-y-2">
          {exportFormats.map((format) => (
            <div
              key={format.value}
              className={`relative rounded-lg border-2 p-3 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                exportFlow.exportFormat === format.value ? 'border-[#5C33FF] bg-[#F5F3FF]' : 'border-[#E4E4E7] bg-[#FAFAFA]'
              }`}
              onClick={() => exportFlow.setExportFormat(format.value)}
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  exportFlow.setExportFormat(format.value)
                }
              }}
            >
              <RadioField>
                <Radio value={format.value} color="violet" data-slot="control" />
                <div data-slot="label" className="flex flex-col gap-1 flex-1">
                  <div className={`${exportFlow.exportFormat === format.value ? 'font-semibold' : 'font-medium'} text-sm text-black`}>
                    {format.label}
                  </div>
                  <div className="text-sm font-medium text-[#71717A]">
                    {format.description}
                  </div>
                </div>
              </RadioField>
              {format.value === 'react' && (
                <Badge className="absolute top-1/2 right-3 -translate-y-1/2 inline-flex items-center rounded-full !bg-[#D1FAE5] px-1.5 py-0.5 text-[10px] font-semibold !text-[#047857]">
                  New
                </Badge>
              )}
            </div>
          ))}
        </RadioGroup>
      </div>

      <div className="pt-2 px-4">
        <Button
          plain
          disabled={exportFlow.isExporting}
          className="w-full !bg-[#5C33FF] hover:!bg-[#5C33FF]/90 !rounded-lg !text-sm !text-white !font-semibold !py-2 !items-center !justify-center data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2 disabled:opacity-50"
          onClick={exportFlow.exportProject}
        >
          {exportButtonText}
          <ArrowDownIcon className="!w-4 !h-4 !text-white !ml-1" />
        </Button>
      </div>
    </div>
  )
}

